import MarginToolCard from '@AMIEWEB/MarginTool/Common/MarginToolCard';
import React, { useCallback, useEffect, useState } from 'react';
import { MarginToolCardTitle } from '../../../Common/MarginToolCardTitle';
import { CircularProgress, Divider, Grid, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MarginToolCurrencyField } from '@AMIEWEB/MarginTool/Common/MarginToolCurrencyField';
import { MarginToolTextArea } from '@AMIEWEB/MarginTool/Common/MarginToolTextArea';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedPlacementMarginScenario } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { PlacementMarginPayPackageStatus } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.model';
import { getValueAsFloat } from '../BillRates/helper';
import { CustomTooltip } from '@AMIEWEB/Common';
import { getStatusUpdateButtonDisabledTooltip } from '../../PayPackage/PayPackageHelper';
import { useIsFieldDisabledInPendingStage } from '../../PayPackage/AddScenario/helper';
import { IPlacementMarginDetailsResponseScenarioSplitItem } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.model';
import { usePeopleSoftCalculation } from '../../PeopleSoftCalculation/IPeopleSoftCalculationHelper';
import { useDebouncedTriggerCalculation, useInitialMarginDetails } from '@AMIEWEB/MarginTool/hooks';
import { selectInitialPlacementMarginScenarioMarginDetailsResponse } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.selector';
import { findInitialSplitValueBySplitId } from '@AMIEWEB/MarginTool/helper';
import { nonTLAHelperText } from '../AllowancesPerDiems/helper';
import { placementMarginScenarioActions } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { usePromiseTracker } from 'react-promise-tracker';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';

export const AdditionalCosts = ({
  splitIndex,
  field,
}: {
  splitIndex: number;
  field: IPlacementMarginDetailsResponseScenarioSplitItem;
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { setValue, formState: { errors }, setError, clearErrors, watch } = useFormContext();
  const { triggerPeopleSoftCalculation } = usePeopleSoftCalculation();
  const { debouncedTriggerPeopleSoftCalculation } = useDebouncedTriggerCalculation();
  const scenario = useSelector(selectSelectedPlacementMarginScenario);
  const isFieldDisabledInPendingStageForUserRoles = useIsFieldDisabledInPendingStage();
  const isScenarioStatusPending = scenario?.scenarioStatusId === PlacementMarginPayPackageStatus.Pending;
  const splitData = scenario?.splits[splitIndex];
  const miscellaneousDescriptionError = errors?.assignmentSplits?.[splitIndex]?.miscellaneousDescription;
  const isAdditionalCostsDisabled = !isScenarioStatusPending || isFieldDisabledInPendingStageForUserRoles;
  const { initiateRefreshCall } = useInitialMarginDetails();
  const [isRefreshInProgress, setIsRefreshInProgress] = useState<boolean>(false);
  const initialScenarioResponse = useSelector(selectInitialPlacementMarginScenarioMarginDetailsResponse);
  const cfSupervisorCosts = watch(`assignmentSplits.${splitIndex}.cfSupervisorCosts`);
  const { promiseInProgress: isGSACalculateFetchDataInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData,
    delay: 0,
  });

  const { promiseInProgress: isPeopleSoftCallMarginToolInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress,
    delay: 0,
  });

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(true));
    if (e?.target?.value?.length >= 1000) {
      setValue(`assignmentSplits.${splitIndex}.miscellaneousDescription`, e?.target?.value.substring(0, 1000));
      setError(`assignmentSplits.${splitIndex}.miscellaneousDescription`, {
        message: t('marginTool.labels.maxLimitReachedTextArea'),
      });
    } else {
      setValue(`assignmentSplits.${splitIndex}.miscellaneousDescription`, e?.target?.value);
      clearErrors(`assignmentSplits.${splitIndex}.miscellaneousDescription`);
    }
    dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(false));
  };

  const handleFieldUpdatesAfterRefresh = useCallback(
    (initialSplit: IPlacementMarginDetailsResponseScenarioSplitItem) => {
      dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(true));
      setValue(`assignmentSplits.${splitIndex}.cfSupervisorCosts`, getValueAsFloat(initialSplit?.csfSupervisorCosts), {
        shouldDirty: true,
      });
      setValue(
        `assignmentSplits.${splitIndex}.miscellaneousAmount`,
        getValueAsFloat(initialSplit?.additionalCostMiscellaneousAmount),
        { shouldDirty: true },
      );
      setValue(
        `assignmentSplits.${splitIndex}.miscellaneousDescription`,
        initialSplit?.additionalCostMiscellaneousDescription ?? '',
        { shouldDirty: true },
      );
      dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(false));
      debouncedTriggerPeopleSoftCalculation();
      setIsRefreshInProgress(false);
    },
    [setValue, debouncedTriggerPeopleSoftCalculation, splitIndex, setIsRefreshInProgress],
  );

  useEffect(() => {
    if (isRefreshInProgress && initialScenarioResponse && !isAdditionalCostsDisabled) {
      const initialSplit = findInitialSplitValueBySplitId(initialScenarioResponse, field?.splitId);
      if (initialSplit) handleFieldUpdatesAfterRefresh(initialSplit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScenarioResponse, isRefreshInProgress, splitIndex]);

  return (
    <MarginToolCard
      id="marginToool-additionalCost"
      title={<MarginToolCardTitle title={t('marginTool.components.assignment.additionalCosts.title')} />}
      showRefreshButton={!isRefreshInProgress}
      icon={<CircularProgress size={16} color="inherit" />}
      isCustomIcon={isRefreshInProgress}
      onRefresh={() => {
        if (!isAdditionalCostsDisabled) {
          initiateRefreshCall();
          setIsRefreshInProgress(true);
        }
      }}
      customBackGroundColor={theme.palette.framework.system.white}
      children={
        <Grid sx={{ p: 2 }}>
          <Grid container item direction="row" spacing={4}>
            <Grid item xs={4}>
              <CustomTooltip
                tooltipContent={isFieldDisabledInPendingStageForUserRoles ? getStatusUpdateButtonDisabledTooltip() : ''}
              >
                <MarginToolCurrencyField
                  id="marginTool-additionalCost-cfsupervisorCosts-currencyField"
                  label={t('marginTool.labels.cfSupervisorCosts')}
                  name={`assignmentSplits.${splitIndex}.cfSupervisorCosts`}
                  disabled={
                    isAdditionalCostsDisabled ||
                    isGSACalculateFetchDataInProgress ||
                    isPeopleSoftCallMarginToolInProgress
                  }
                  defaultValue={getValueAsFloat(splitData?.csfSupervisorCosts)}
                  tooltip={{ disabled: true }}
                  endAdornmentValue={t('marginTool.labels.perDay')}
                  helperText={nonTLAHelperText(cfSupervisorCosts, t)}
                  triggerMarginToolFormUpdates={() => triggerPeopleSoftCalculation()}
                />
              </CustomTooltip>
            </Grid>
          </Grid>
          <Grid sx={{ pt: 2 }}>
            <Divider />
          </Grid>
          <Grid container spacing={4} sx={{ pt: 2 }}>
            <Grid item xs={4}>
              <CustomTooltip
                tooltipContent={isFieldDisabledInPendingStageForUserRoles ? getStatusUpdateButtonDisabledTooltip() : ''}
              >
                <MarginToolCurrencyField
                  id="marginTool-additionalCost-miscellaneousAmount-currencyField"
                  label={t('marginTool.labels.miscellaneousAmount')}
                  name={`assignmentSplits.${splitIndex}.miscellaneousAmount`}
                  disabled={
                    isAdditionalCostsDisabled ||
                    isGSACalculateFetchDataInProgress ||
                    isPeopleSoftCallMarginToolInProgress
                  }
                  defaultValue={getValueAsFloat(splitData?.additionalCostMiscellaneousAmount)}
                  tooltip={{ disabled: true }}
                  triggerMarginToolFormUpdates={() => triggerPeopleSoftCalculation()}
                />
              </CustomTooltip>
            </Grid>
          </Grid>
          <Grid container spacing={4} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <CustomTooltip
                tooltipContent={isFieldDisabledInPendingStageForUserRoles ? getStatusUpdateButtonDisabledTooltip() : ''}
                placement={'bottom-start'}
              >
                <MarginToolTextArea
                  id="marginTool-additionalCost-miscellaneousDescription-textField"
                  label={t('marginTool.labels.miscellaneousDescription')}
                  name={`assignmentSplits.${splitIndex}.miscellaneousDescription`}
                  disabled={
                    isAdditionalCostsDisabled ||
                    isGSACalculateFetchDataInProgress ||
                    isPeopleSoftCallMarginToolInProgress
                  }
                  error={!!miscellaneousDescriptionError}
                  helperText={miscellaneousDescriptionError ? miscellaneousDescriptionError?.message : undefined}
                  defaultValue={field?.additionalCostMiscellaneousDescription}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleTextChange(event)}
                  inputProps={{ maxLength: 1000 }}
                />
              </CustomTooltip>
            </Grid>
          </Grid>
        </Grid>
      }
    />
  );
};
